import { createScheduleComponent } from '@play-co/components';
import { Gacha } from '../data/Gachas.schema';
import { GachaBucket } from '../data/GachaBuckets.schema';
import {
  DecorationItemID,
  DecorationRoomID,
  DecorationThemeID,
  DecorationTierID,
} from '../decoration/decoration.ruleset';
import { AccessoryTierID } from '../data/AccessoryTiers.schema';
import {
  AccessoryItemID,
  AccessoryThemeID,
} from '../data/AccessoryItems.schema';
import GachaData from '../data/Gachas.json';
import GachaBucketsData from '../data/GachaBuckets.json';
import { PromoID } from '../promo/promo.ruleset';
import { BackgroundEffect } from '../match2/match2.background.ruleset';

export type GachaEventTheme =
  | 'setsubun'
  | 'valentines'
  | 'cafe'
  | 'hanami'
  | 'childrens'
  | 'mothersday'
  | 'melonseason'
  | 'tanabata'
  | 'kakigori'
  | 'moonviewing2025'
  | 'magicalbooks'
  | 'tasteautumn'
  | 'wintercandle'
  | 'icequeenscastle'
  | 'chocolatier'
  | 'valentinesday'
  | 'springteaparty'
  | 'easteregghunt'
  | 'goldenweek'
  | 'rainyseasonbookcafe'
  | 'tropicalislandvacation'
  | 'seashell'
  | 'earlyautumnpicnic'
  | 'pumpkinfarm';
export type { Gacha, GachaBucket };
export type GachaID = Gacha['id'];
export type GachaBucketID = GachaBucket['id'];
export type GachaTags = Exclude<Gacha['tags'], undefined>;
export type GachaTag = GachaTags[number];

/**
 * List of gachas.
 */
export const gachas = GachaData as unknown as Gacha[];

/**
 * Gachas indexed by id.
 */
export const gachasById = gachas.reduce(
  (acc, gacha) => {
    acc[gacha.id] = gacha;
    return acc;
  },
  {} as Record<GachaID, Gacha>,
);

/**
 * List of gacha buckets.
 */
export const gachaBuckets = GachaBucketsData as GachaBucket[];

/**
 * Buckets indexed by id.
 */
export const gachaBucketsById = gachaBuckets.reduce(
  (acc, bucket) => {
    acc[bucket.id] = bucket;
    return acc;
  },
  {} as Record<GachaBucketID, GachaBucket>,
);

/**
 * Gacha item.
 */
export type GachaItem =
  | {
      type: 'decoration';
      tier: number;
      tierId: DecorationTierID;
      id: DecorationItemID;
      image?: string;
      name_en: string;
      name_jp: string;
      theme: DecorationThemeID;
      room: DecorationRoomID;
    }
  | {
      type: 'accessory';
      tier: number;
      tierId: AccessoryTierID;
      id: AccessoryItemID;
      image?: string;
      name_en: string;
      name_jp: string;
      theme: AccessoryThemeID;
    };

type GachaEventMetadata = {
  theme: GachaEventTheme;
  tag: GachaTag;
  promo: PromoID;
  decorationThemeId: DecorationThemeID;
  /** Optional puzzle background filename for match2 (e.g., 'BG_artisanchocolatier.png') */
  puzzleBackground?: string;
  /** Optional particle effect for puzzle background */
  puzzleBackgroundEffect?: BackgroundEffect;
};

// NB: make sure the metadata is a gacha theme
export const gachaEventSchedule = createScheduleComponent<GachaEventMetadata>(
  '2024-04-26',
  'Asia/Tokyo',
  {
    once: [
      {
        at: '2025-01-31T21:00:00',
        // Ends at: 2025-02-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'setsubun',
          tag: 'setsubun',
          promo: '2025-setsubun-gacha',
          decorationThemeId: '27_setsubun',
        },
      },
      {
        at: '2025-02-14T12:00:00',
        // Ends at: 2025-02-17 12:00 per spec
        duration: { days: 3 },
        metadata: {
          theme: 'valentines',
          tag: 'valentines',
          promo: '2025-valentines-gacha',
          decorationThemeId: '09_valentine_day',
        },
      },
      {
        at: '2025-02-28T21:00:00',
        // Ends at: 2025-03-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'cafe',
          tag: 'cafe',
          promo: '2025-cafe-gacha',
          decorationThemeId: '28_cafe',
        },
      },
      {
        at: '2025-03-31T21:00:00',
        // Ends at: 2025-04-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'hanami',
          tag: 'hanami',
          promo: '2025-hanami-gacha',
          decorationThemeId: '29_hanami',
        },
      },
      {
        at: '2025-04-30T21:00:00',
        // Ends at: 2025-05-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'childrens',
          tag: 'childrens_day2025',
          promo: '2025-childrens-day-gacha',
          decorationThemeId: '30_childrens_day2025',
        },
      },
      {
        at: '2025-05-09T00:00:00',
        // Ends at: 2025-05-12 23:59 per spec
        duration: { days: 3, hours: 23, minutes: 59 },
        metadata: {
          theme: 'mothersday',
          tag: 'mothers_day2025',
          promo: '2025-mothers-day-gacha',
          decorationThemeId: '31_mothers_day2025',
        },
      },
      {
        at: '2025-05-31T21:00:00',
        // Ends at: 2025-06-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'melonseason',
          tag: 'melon_season',
          promo: '2025-melon-season-gacha',
          decorationThemeId: '32_melon_season',
        },
      },
      {
        at: '2025-06-30T21:00:00',
        // Ends at: 2025-07-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'tanabata',
          tag: 'tanabata',
          promo: '2025-tanabata-gacha',
          decorationThemeId: '33_tanabata',
        },
      },
      {
        at: '2025-07-31T21:00:00',
        // Ends at: 2025-08-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'kakigori',
          tag: 'kakigori',
          promo: '2025-kakigori-gacha',
          decorationThemeId: '34_kakigori',
        },
      },
      {
        at: '2025-08-31T21:00:00',
        // Ends at: 2025-09-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'moonviewing2025',
          tag: 'moonviewing2025',
          promo: '2025-moonviewing2025-gacha',
          decorationThemeId: '35_moonviewing2025',
        },
      },
      {
        at: '2025-09-30T21:00:00',
        // Ends at: 2025-10-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'magicalbooks',
          tag: 'magicalbooks',
          promo: '2025-magical-books-gacha',
          decorationThemeId: '36_magicalbooks',
        },
      },
      {
        at: '2025-10-31T21:00:00',
        // Ends at: 2025-11-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'tasteautumn',
          tag: 'taste_autumn',
          promo: '2025-taste-autumn-gacha',
          decorationThemeId: '37_taste_autumn',
        },
      },
      {
        at: '2025-11-30T21:00:00',
        // Ends at: 2025-12-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'wintercandle',
          tag: 'winter_candle',
          promo: '2025-winter-candle-gacha',
          decorationThemeId: '38_winter_candle',
        },
      },
      {
        at: '2025-12-31T21:00:00',
        // Ends at: 2026-01-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'icequeenscastle',
          tag: 'ice_queens_castle',
          promo: '2025-ice-queens-castle-gacha',
          decorationThemeId: '39_ice_queens_castle',
        },
      },
      {
        at: '2026-01-31T21:00:00',
        // Ends at: 2026-02-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'chocolatier',
          tag: 'artisan_chocolatier',
          promo: '2026-artisan-chocolatier-gacha',
          decorationThemeId: '40_artisan_chocolatier',
          puzzleBackground: 'BG_artisanchocolatier.png',
        },
      },
      {
        at: '2026-02-12T21:00:00',
        // Ends at: 2026-02-15 22:59 per spec
        duration: { days: 3, hours: 1, minutes: 59 },
        metadata: {
          theme: 'valentinesday',
          tag: 'valentine_day',
          promo: '2026-valentine-day-gacha',
          decorationThemeId: '41_valentine_day',
        },
      },
      {
        at: '2026-02-28T21:00:00',
        // Ends at: 2026-03-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'springteaparty',
          tag: 'spring_tea_party',
          promo: '2026-spring-tea-party-gacha',
          decorationThemeId: '42_spring_tea_party',
          puzzleBackground: 'BG_springteaparty.png',
          puzzleBackgroundEffect: 'sakura',
        },
      },
      {
        at: '2026-03-31T21:00:00',
        // Ends at: 2026-04-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'easteregghunt',
          tag: 'easter_egg_hunt',
          promo: '2026-easter-egg-hunt-gacha',
          decorationThemeId: '43_easter_egg_hunt',
          puzzleBackground: 'BG_easteregghunt.png',
        },
      },
      {
        at: '2026-04-30T21:00:00',
        // Ends at: 2026-05-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'goldenweek',
          tag: 'golden_week',
          promo: '2026-golden-week-gacha',
          decorationThemeId: '44_golden_week',
          puzzleBackground: 'BG_goldenweek2026.png',
        },
      },
      {
        at: '2026-05-30T21:00:00',
        // Ends at: 2026-06-08 22:59 per spec
        duration: { days: 9, hours: 1, minutes: 59 },
        metadata: {
          theme: 'rainyseasonbookcafe',
          tag: 'rainyseason_bookcafe',
          promo: '2026-rainyseason-bookcafe-gacha',
          decorationThemeId: '45_rainyseason_bookcafe',
          puzzleBackground: 'BG_rainyseasonbookcafe_June.png',
        },
      },
      {
        at: '2026-06-30T21:00:00',
        // Ends at: 2026-07-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'tropicalislandvacation',
          tag: 'tropical_island_vacation',
          promo: '2026-tropical-island-vacation-gacha',
          decorationThemeId: '46_tropical_island_vacation',
          puzzleBackground: 'BG_tropicalislandvacation.png',
        },
      },
      {
        at: '2026-07-31T21:00:00',
        // Ends at: 2026-08-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'seashell',
          tag: 'seashell',
          promo: '2026-seashell-gacha',
          decorationThemeId: '47_seashell',
          puzzleBackground: 'BG_seashellcollection.png',
        },
      },
      {
        at: '2026-08-31T21:00:00',
        // Ends at: 2026-09-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'earlyautumnpicnic',
          tag: 'early_autumn_picnic',
          promo: '2026-early-autumn-picnic-gacha',
          decorationThemeId: '48_early_autumn_picnic',
          puzzleBackground: 'BG_earlyautumnpicnic.png',
        },
      },
      {
        at: '2026-09-30T21:00:00',
        // Ends at: 2026-10-08 22:59 per spec
        duration: { days: 8, hours: 1, minutes: 59 },
        metadata: {
          theme: 'pumpkinfarm',
          tag: 'pumpkin_farm',
          promo: '2026-pumpkin-farm-gacha',
          decorationThemeId: '49_pumpkin_farm',
          puzzleBackground: 'BG_pumpkinfarm.png',
        },
      },
    ],
  },
);
